import { Fragment, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import 'react-datepicker/dist/react-datepicker.css';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { setAlert } from '../../../actions/alert';
import { obtenerDetalleProyecto, limpiarDetalleProyecto } from '../../../actions/proyectos';
import {
    buscarInventarioEntregado,
    limpiarInventarioEntregado,
    crearOrden
} from '../../../actions/ordenesDevolucion';

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const formatYYYYMMDD = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

/* El mismo producto+unidad puede venir de distintas órdenes de salida del proyecto — la clave
   debe incluir orden_salida para no mezclar sus disponibles ni perder a cuál pertenece cada fila. */
const buildKey = (idOrdenSalida, idProducto, idUnidad) => `${idOrdenSalida || 'na'}_${idProducto}_${idUnidad || 'na'}`;

const NuevaOrdenDevolucion = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { idProyecto } = useParams();

    const { detalle: proyecto } = useSelector(state => state.proyectos);
    const { inventarioEntregado, paquetesEntregado, loadingInventarioEntregado } = useSelector(state => state.ordenesDevolucion);

    const [fecha, setFecha] = useState(new Date());
    const [observaciones, setObservaciones] = useState('');

    const [productoSel, setProductoSel] = useState(null);
    const [cantidadProducto, setCantidadProducto] = useState('');

    const [productos, setProductos] = useState([]);

    const [paqueteSel, setPaqueteSel] = useState(null);
    const [cantidadPaquete, setCantidadPaquete] = useState('');

    const [paquetes, setPaquetes] = useState([]);

    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (!idProyecto) return;

        dispatch(obtenerDetalleProyecto(idProyecto));
        dispatch(buscarInventarioEntregado(idProyecto));

        return () => {
            dispatch(limpiarDetalleProyecto());
            dispatch(limpiarInventarioEntregado());
        };
    }, [dispatch, idProyecto]);

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: false, nombre: 'Proyectos', path: '/proyectos' },
            { activo: false, nombre: proyecto?.nombre || 'Proyecto', path: `/proyectos/detalle/${idProyecto}` },
            { activo: true, nombre: 'Nueva orden de devolución', path: `/proyectos/devoluciones/nueva/${idProyecto}` }
        ]));
    }, [dispatch, idProyecto, proyecto]);

    const foliosPorOrdenSalida = useMemo(() => {
        const mapa = {};
        (proyecto?.ordenes || []).forEach((o) => { mapa[String(o._id)] = o.folio; });
        return mapa;
    }, [proyecto]);

    const opcionesProductos = useMemo(() => {
        return (inventarioEntregado || []).map(item => {
            const folio = foliosPorOrdenSalida[String(item.orden_salida)];
            return {
                value: buildKey(item.orden_salida, item.producto?._id, item.unidad?._id),
                label: `${item.producto?.nombre || 'Sin definir'} (${item.unidad?.nombre || 'Sin unidad'}) — Orden ${folio || 'Sin definir'} — Disponible: ${formatNumber(item.cantidad_disponible)}`,
                raw: item
            };
        });
    }, [inventarioEntregado, foliosPorOrdenSalida]);

    const disponibleSeleccionado = Number(productoSel?.raw?.cantidad_disponible || 0);

    const opcionesPaquetes = useMemo(() => {
        return (paquetesEntregado || []).map(item => ({
            value: String(item.orden_salida_paquete),
            label: `${item.paquete?.nombre || 'Sin definir'} — Disponible: ${formatNumber(item.cantidad_disponible)}`,
            raw: item
        }));
    }, [paquetesEntregado]);

    const disponiblePaqueteSeleccionado = Number(paqueteSel?.raw?.cantidad_disponible || 0);

    const limpiarFormularioProducto = () => {
        setProductoSel(null);
        setCantidadProducto('');
    };

    const puedeAgregarProducto = useMemo(() => {
        return Boolean(productoSel?.value && Number(cantidadProducto) > 0 && Number(cantidadProducto) <= disponibleSeleccionado);
    }, [productoSel, cantidadProducto, disponibleSeleccionado]);

    const onAgregarProducto = () => {
        if (!puedeAgregarProducto) return;

        const item = productoSel.raw;
        const idProducto = item.producto?._id;
        const idUnidad = item.unidad?._id;
        const idOrdenSalida = item.orden_salida;

        setProductos((prev) => {
            const idx = prev.findIndex(p => p.key === productoSel.value);

            if (idx === -1) {
                return [
                    ...prev,
                    {
                        key: productoSel.value,
                        ordenSalida: idOrdenSalida,
                        producto: idProducto,
                        productoNombre: item.producto?.nombre || 'Sin definir',
                        unidad: idUnidad,
                        unidadNombre: item.unidad?.nombre || 'Sin unidad',
                        disponible: Number(item.cantidad_disponible || 0),
                        cantidad: Number(cantidadProducto)
                    }
                ];
            }

            const copy = [...prev];
            const nuevaCantidad = Math.min(
                Number(copy[idx].cantidad || 0) + Number(cantidadProducto),
                Number(copy[idx].disponible || 0)
            );
            copy[idx] = { ...copy[idx], cantidad: nuevaCantidad };
            return copy;
        });

        limpiarFormularioProducto();
    };

    const onEliminarProducto = (key) => {
        setProductos((prev) => prev.filter(p => p.key !== key));
    };

    const onChangeCantidadFila = (key, value) => {
        setProductos((prev) => prev.map(p => {
            if (p.key !== key) return p;
            if (value === '') return { ...p, cantidad: '' };
            const clamped = Math.max(0, Math.min(Number(value), Number(p.disponible || 0)));
            return { ...p, cantidad: clamped };
        }));
    };

    const limpiarFormularioPaquete = () => {
        setPaqueteSel(null);
        setCantidadPaquete('');
    };

    const puedeAgregarPaquete = useMemo(() => {
        return Boolean(paqueteSel?.value && Number(cantidadPaquete) > 0 && Number(cantidadPaquete) <= disponiblePaqueteSeleccionado);
    }, [paqueteSel, cantidadPaquete, disponiblePaqueteSeleccionado]);

    const onAgregarPaquete = () => {
        if (!puedeAgregarPaquete) return;

        const item = paqueteSel.raw;
        const idOrdenSalidaPaquete = item.orden_salida_paquete;

        setPaquetes((prev) => {
            const idx = prev.findIndex(p => p.orden_salida_paquete === String(idOrdenSalidaPaquete));

            if (idx === -1) {
                return [
                    ...prev,
                    {
                        key: paqueteSel.value,
                        orden_salida_paquete: String(idOrdenSalidaPaquete),
                        paqueteNombre: item.paquete?.nombre || 'Sin definir',
                        paqueteCodigo: item.paquete?.codigo || null,
                        disponible: Number(item.cantidad_disponible || 0),
                        cantidad: Number(cantidadPaquete)
                    }
                ];
            }

            const copy = [...prev];
            const nuevaCantidad = Math.min(
                Number(copy[idx].cantidad || 0) + Number(cantidadPaquete),
                Number(copy[idx].disponible || 0)
            );
            copy[idx] = { ...copy[idx], cantidad: nuevaCantidad };
            return copy;
        });

        limpiarFormularioPaquete();
    };

    const onEliminarPaquete = (key) => {
        setPaquetes((prev) => prev.filter(p => p.key !== key));
    };

    const onChangeCantidadFilaPaquete = (key, value) => {
        setPaquetes((prev) => prev.map(p => {
            if (p.key !== key) return p;
            if (value === '') return { ...p, cantidad: '' };
            const clamped = Math.max(0, Math.min(Number(value), Number(p.disponible || 0)));
            return { ...p, cantidad: clamped };
        }));
    };

    const puedeGuardar = useMemo(() => {
        return Boolean(
            idProyecto &&
            (productos.length > 0 || paquetes.length > 0) &&
            productos.every(p => Number(p.cantidad) > 0) &&
            paquetes.every(p => Number(p.cantidad) > 0) &&
            !guardando
        );
    }, [idProyecto, productos, paquetes, guardando]);

    const onGuardar = async () => {
        if (!puedeGuardar) return;

        if (productos.length === 0 && paquetes.length === 0) {
            dispatch(setAlert('Agrega al menos un producto o paquete', 'danger'));
            return;
        }

        setGuardando(true);
        const orden = await dispatch(crearOrden({
            id_proyecto: idProyecto,
            fecha: formatYYYYMMDD(fecha),
            observaciones: observaciones.trim() || null,
            productos: productos.map(p => ({
                producto: p.producto,
                unidad: p.unidad,
                cantidad: p.cantidad,
                orden_salida: p.ordenSalida
            })),
            paquetes: paquetes.map(p => ({
                orden_salida_paquete: p.orden_salida_paquete,
                cantidad: p.cantidad
            }))
        }));
        setGuardando(false);

        if (!orden) return;

        navigate(`/proyectos/detalle/${idProyecto}`);
    };

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5 mb-1">Nueva orden de devolución</h3>
                    <h6 className="text-muted mb-4">{proyecto?.nombre || 'Proyecto'}</h6>

                    <div className="row g-3 mb-4">
                        <div className="col-md-3">
                            <div className="text-muted small mb-1">Fecha</div>
                            <DatePicker
                                selected={fecha}
                                onChange={(d) => setFecha(d)}
                                className="form-control"
                                dateFormat="dd/MM/yyyy"
                            />
                        </div>
                        <div className="col-md-9">
                            <div className="text-muted small mb-1">Observaciones</div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Observaciones (opcional)"
                                value={observaciones}
                                onChange={(e) => setObservaciones(e.target.value)}
                            />
                        </div>
                    </div>

                    <h5 className="mb-3">Productos</h5>

                    <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                        Solo se listan productos entregados mediante órdenes de salida completadas de este proyecto, con cantidad disponible para devolver.
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-md-8">
                            <div className="text-muted small mb-1">Producto entregado</div>
                            <Select
                                options={opcionesProductos}
                                value={productoSel}
                                onChange={(opt) => { setProductoSel(opt || null); setCantidadProducto(''); }}
                                isClearable
                                isLoading={loadingInventarioEntregado}
                                placeholder="Producto entregado..."
                                noOptionsMessage={() => 'Sin productos disponibles para devolver'}
                            />
                        </div>
                        <div className="col-md-2">
                            <div className="text-muted small mb-1">Cantidad</div>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Cantidad"
                                value={cantidadProducto}
                                min={0}
                                max={disponibleSeleccionado || undefined}
                                onChange={(e) => setCantidadProducto(e.target.value)}
                                disabled={!productoSel}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-center justify-content-end">
                            <button
                                type="button"
                                className="btn btn-outline-primary w-100"
                                onClick={onAgregarProducto}
                                disabled={!puedeAgregarProducto}
                            >
                                <i className="feather icon-plus" /> Agregar
                            </button>
                        </div>
                    </div>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th>Unidad</th>
                                    <th>Orden de salida</th>
                                    <th className="text-end">Cantidad a devolver</th>
                                    <th className="text-end">Disponible</th>
                                    <th className="text-end">Opciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productos.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted">
                                            Sin productos agregados
                                        </td>
                                    </tr>
                                )}

                                {productos.map((p) => (
                                    <tr key={p.key}>
                                        <td>{p.productoNombre}</td>
                                        <td>{p.unidadNombre}</td>
                                        <td>{foliosPorOrdenSalida[String(p.ordenSalida)] || 'Sin definir'}</td>
                                        <td className="text-end" style={{ minWidth: 110 }}>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm text-end"
                                                value={p.cantidad}
                                                min={0}
                                                max={p.disponible}
                                                onChange={(e) => onChangeCantidadFila(p.key, e.target.value)}
                                            />
                                        </td>
                                        <td className="text-end">{formatNumber(p.disponible)}</td>
                                        <td className="text-end">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link px-2"
                                                title="Eliminar"
                                                onClick={() => onEliminarProducto(p.key)}
                                            >
                                                <i className="fas fa-trash text-danger" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <h5 className="mb-3">Paquetes</h5>

                    <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                        Paquetes entregados completos en una orden de salida completada, con cantidad disponible para devolver. Solo se pueden
                        devolver paquetes completos, no productos sueltos de su interior.
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-md-8">
                            <div className="text-muted small mb-1">Paquete entregado</div>
                            <Select
                                options={opcionesPaquetes}
                                value={paqueteSel}
                                onChange={(opt) => { setPaqueteSel(opt || null); setCantidadPaquete(''); }}
                                isClearable
                                isLoading={loadingInventarioEntregado}
                                placeholder="Paquete entregado..."
                                noOptionsMessage={() => 'Sin paquetes disponibles para devolver'}
                            />
                        </div>
                        <div className="col-md-2">
                            <div className="text-muted small mb-1">Cantidad</div>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Cantidad"
                                value={cantidadPaquete}
                                min={0}
                                max={disponiblePaqueteSeleccionado || undefined}
                                onChange={(e) => setCantidadPaquete(e.target.value)}
                                disabled={!paqueteSel}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-center justify-content-end">
                            <button
                                type="button"
                                className="btn btn-outline-primary w-100"
                                onClick={onAgregarPaquete}
                                disabled={!puedeAgregarPaquete}
                            >
                                <i className="feather icon-plus" /> Agregar
                            </button>
                        </div>
                    </div>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Paquete</th>
                                    <th className="text-end">Cantidad a devolver</th>
                                    <th className="text-end">Disponible</th>
                                    <th className="text-end">Opciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paquetes.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center text-muted">
                                            Sin paquetes agregados
                                        </td>
                                    </tr>
                                )}

                                {paquetes.map((p) => (
                                    <tr key={p.key}>
                                        <td>
                                            {p.paqueteNombre}
                                            {p.paqueteCodigo ? (
                                                <div className="text-muted" style={{ fontSize: 12 }}>{p.paqueteCodigo}</div>
                                            ) : null}
                                        </td>
                                        <td className="text-end" style={{ minWidth: 110 }}>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm text-end"
                                                value={p.cantidad}
                                                min={0}
                                                max={p.disponible}
                                                onChange={(e) => onChangeCantidadFilaPaquete(p.key, e.target.value)}
                                            />
                                        </td>
                                        <td className="text-end">{formatNumber(p.disponible)}</td>
                                        <td className="text-end">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link px-2"
                                                title="Eliminar"
                                                onClick={() => onEliminarPaquete(p.key)}
                                            >
                                                <i className="fas fa-trash text-danger" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-end">
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate(`/proyectos/detalle/${idProyecto}`)}
                                disabled={guardando}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={onGuardar}
                                disabled={!puedeGuardar}
                            >
                                Guardar orden
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default NuevaOrdenDevolucion;
